import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, AlertCircle, Check } from "lucide-react";
import type { GeneratingItem } from "@/store/creativeGenerationStore";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface GeneratingPlaceholderCardProps {
  item: GeneratingItem;
  isVideo?: boolean;
}

export function GeneratingPlaceholderCard({ item, isVideo = false }: GeneratingPlaceholderCardProps) {
  const { language } = useAuth();
  const lang = language as Language;
  // Track previous status to animate transitions
  const [showImage, setShowImage] = useState(false);

  // When done, delay showing the image slightly for a smooth reveal
  useEffect(() => {
    if (item.status === "done" && item.imageUrl) {
      const img = new Image();
      img.src = item.imageUrl;
      img.onload = () => setShowImage(true);
      // Fallback in case onload doesn't fire
      const timeout = setTimeout(() => setShowImage(true), 500);
      return () => clearTimeout(timeout);
    }
  }, [item.status, item.imageUrl]);

  // Error state
  if (item.status === "error") {
    return (
      <motion.div
        layout
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative overflow-hidden rounded-xl bg-red-50/50 border border-red-200 aspect-square flex flex-col items-center justify-center p-4 text-center"
      >
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mb-3">
          <AlertCircle className="h-5 w-5 text-red-400" />
        </div>
        <p className="text-sm font-semibold text-red-600 mb-1">
          {dictionary["error-generating"]?.[lang] || "Error generating"}
        </p>
        <p className="text-xs text-red-400 leading-relaxed">
          {item.errorMessage || dictionary["try-again-later"]?.[lang] || "Try again later"}
        </p>
      </motion.div>
    );
  }

  // Done state — smooth image reveal
  if (item.status === "done" && item.imageUrl) {
    return (
      <motion.div
        layout
        className="relative overflow-hidden rounded-xl bg-card border border-border/40 transition-colors duration-500 group"
      >
        <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-b from-[#F8F8FF] to-white">
          <AnimatePresence>
            {showImage && (
              <motion.img
                key="done-image"
                src={item.imageUrl}
                alt={item.productName}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
          </AnimatePresence>

          {/* Success badge — fades in after image */}
          {showImage && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-green-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm"
            >
              <Check className="h-3 w-3" />
              {dictionary["done"]?.[lang] || "Done"}
            </motion.div>
          )}

          {/* Keep spinner visible until image loads */}
          {!showImage && (
            <div className="absolute inset-0 flex items-center justify-center">
              <SpinnerIcon />
            </div>
          )}
        </div>
        <div className="px-3 py-2.5 flex items-center gap-2 border-t border-border/30">
          {item.productImageUrl && (
            <div className="h-5 w-5 rounded-full overflow-hidden flex-shrink-0 bg-muted/50 border border-border/30">
              <img src={item.productImageUrl} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <span className="text-sm font-medium text-foreground truncate leading-tight">
            {item.productName}
          </span>
          {showImage && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="ml-auto text-[10px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full"
            >
              {dictionary["new"]?.[lang] || "New"}
            </motion.span>
          )}
        </div>
      </motion.div>
    );
  }

  // Generating state
  return (
    <motion.div
      layout
      className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#F8F8FF] to-white border border-[#E8E8F8] aspect-square shadow-sm"
    >
      {/* Animated shimmer sweep */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(110deg, transparent 25%, rgba(80,80,234,0.04) 45%, rgba(80,80,234,0.08) 50%, rgba(80,80,234,0.04) 55%, transparent 75%)",
        }}
        animate={{ x: ["-150%", "150%"] }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        <SpinnerIcon />
        <p className="text-[13px] font-bold text-gray-800 tracking-tight mt-4">
          {isVideo
            ? (dictionary["generating-video"]?.[lang] || "Generating video")
            : (dictionary["generating-ad"]?.[lang] || "Generating ad")}
        </p>
        <p className="text-[11px] text-gray-400 mt-1 tracking-tight">
          {isVideo
            ? (dictionary["video-generating-wait"]?.[lang] || "This may take a few minutes...")
            : (dictionary["this-may-take-a-moment"]?.[lang] || "This may take a moment...")}
        </p>
      </div>
    </motion.div>
  );
}

/** Reusable spinning circle + sparkles icon */
function SpinnerIcon() {
  const r = 34;
  const circ = 2 * Math.PI * r;

  return (
    <motion.div
      animate={{ scale: [1, 1.06, 1] }}
      transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
      className="relative"
      style={{ width: 72, height: 72 }}
    >
      <motion.svg
        className="absolute inset-0"
        width={72}
        height={72}
        viewBox="0 0 72 72"
        animate={{ rotate: 360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
      >
        <circle cx={36} cy={36} r={r} fill="none" stroke="#E8E8F8" strokeWidth={3} />
        <circle
          cx={36}
          cy={36}
          r={r}
          fill="none"
          stroke="url(#spinGrad)"
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={`${circ * 0.3} ${circ * 0.7}`}
        />
        <defs>
          <linearGradient id="spinGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5050EA" />
            <stop offset="100%" stopColor="#8484F0" />
          </linearGradient>
        </defs>
      </motion.svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#5050EA] to-[#7B7BF7] flex items-center justify-center shadow-lg shadow-[#5050EA]/20">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
      </div>
    </motion.div>
  );
}
